package horcrux.frontend.common.views.collections.importdocuments

import horcrux.common.api.APILimits
import horcrux.common.api.dtos.types.ChunkOverlap
import horcrux.common.api.dtos.types.ChunkSize
import horcrux.common.api.dtos.types.EmbeddingModelConfigs
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.State
import horcrux.frontend.common.views.components.containers.DialogAction
import horcrux.frontend.common.views.components.containers.DialogView
import horcrux.frontend.common.views.components.containers.VStack
import horcrux.frontend.common.views.components.input.sliderWithHelp
import mui.material.DialogContent
import mui.material.DialogTitle
import react.FC
import react.Props

data class EmbeddingModelConfigsState(
    val maxDocumentChunkSizeState: State<ChunkSize>,
    val maxDocumentChunkOverlapState: State<ChunkOverlap>,
) {
    constructor() : this(State(DEFAULT_MAX_DOCUMENT_CHUNK_SIZE), State(DEFAULT_MAX_DOCUMENT_CHUNK_OVERLAP))

    val embeddingModelConfigs: EmbeddingModelConfigs
        get() = EmbeddingModelConfigs(maxDocumentChunkSizeState.get(), maxDocumentChunkOverlapState.get())

    fun reset() {
        maxDocumentChunkSizeState.reset()
        maxDocumentChunkOverlapState.reset()
    }

    companion object {
        private val DEFAULT_MAX_DOCUMENT_CHUNK_SIZE: ChunkSize = ChunkSize(100)
        private val DEFAULT_MAX_DOCUMENT_CHUNK_OVERLAP: ChunkOverlap = ChunkOverlap(10)
    }
}

external interface EditChatModelConfigsDialogProps : Props {
    var showDialogState: BooleanState
    var embeddingModelConfigsState: EmbeddingModelConfigsState
}

val ChunkSizeSliderWithHelp =
    sliderWithHelp({ number -> ChunkSize(number.toInt()) }) { value -> value.value }
val ChunkOverlapSliderWithHelp =
    sliderWithHelp({ number -> ChunkOverlap(number.toInt()) }) { value -> value.value }

val EditEmbeddingModelConfigsDialog = FC<EditChatModelConfigsDialogProps> { props ->
    val title = "Edit Import Documents Settings"
    DialogView {
        this.title = title
        this.showDialogState = props.showDialogState
        this.dialogActionsMaybe = listOf(
            DialogAction("Reset") { _, _ -> props.embeddingModelConfigsState.reset() },
            DialogAction("OK") { _, _ -> props.showDialogState.setFalse() }
        )
        DialogTitle { +title }
        DialogContent {
            VStack {
                ChunkSizeSliderWithHelp {
                    this.id = "chunk-size-slider"
                    this.label = "Max Document Chunk Size"
                    this.min = APILimits.MIN_MAX_CHUNK_SIZE
                    this.max = APILimits.MAX_MAX_CHUNK_SIZE
                    this.step = 1
                    this.valueState = props.embeddingModelConfigsState.maxDocumentChunkSizeState
                    this.helpText =
                        """When you import a document we will break it up into smaller more easily searchable chunks. This controls the maximum number of characters in those chunks. This is a useful setting because smaller chunks can sometimes enable us to use more relevant information to answer your question. """
                }
                ChunkOverlapSliderWithHelp {
                    this.id = "chunk-overlap-slider"
                    this.label = "Max Chunk Overlap"
                    this.min = APILimits.MIN_CHUNK_OVERLAP
                    this.max = APILimits.MAX_CHUNK_OVERLAP
                    this.step = 1
                    this.valueState = props.embeddingModelConfigsState.maxDocumentChunkOverlapState
                    this.helpText =
                        """When you import a document we will break it up into smaller more easily searchable chunks. Having the end of one chunk overlap with the end of the next chunk can help us better answer your question. This setting controls the size of that overlap. """
                }
            }
        }
    }
}
